// NIH Clinical Tables – Medical Conditions + Genetic Diseases
// Conditions: https://clinicaltables.nlm.nih.gov/apidoc/conditions/v3/doc.html
// Genetic diseases (ClinVar): https://clinicaltables.nlm.nih.gov/apidoc/disease_names/v3/doc.html

import firebase from 'firebase/compat/app';
import 'firebase/compat/functions';

const NIH_CONDITIONS_URL = 'https://clinicaltables.nlm.nih.gov/api/conditions/v3/search';
const NIH_DISEASE_NAMES_URL = 'https://clinicaltables.nlm.nih.gov/api/disease_names/v3/search';

function parseConditionNames(data) {
  if (!data || !Array.isArray(data)) return [];
  const displayArrays = Array.isArray(data[3]) ? data[3] : Array.isArray(data[1]) ? data[1] : null;
  if (!displayArrays) return [];
  return displayArrays
    .map((row) => {
      if (!Array.isArray(row)) return null;
      const str = row.length > 1 ? row[row.length - 1] : row[0];
      return str != null ? String(str).trim() : null;
    })
    .filter(Boolean);
}

function parseDiseaseNames(data) {
  if (!data || !Array.isArray(data)) return [];
  const displayArrays = data[1];
  if (!Array.isArray(displayArrays)) return [];
  return displayArrays
    .map((row) => {
      if (!Array.isArray(row)) return row != null ? String(row).trim() : null;
      const str = row.length > 1 ? row[row.length - 1] : row[0];
      return str != null ? String(str).trim() : null;
    })
    .filter(Boolean);
}

export const conditionsSearchService = {
  async searchConditions(query, maxResults = 20) {
    if (!query || typeof query !== 'string') return [];
    const trimmed = query.trim();
    if (trimmed.length === 0) return [];
    const maxList = Math.min(maxResults, 500);

    try {
      const url = `${NIH_CONDITIONS_URL}?terms=${encodeURIComponent(trimmed)}&df=consumer_name&maxList=${maxList}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`NIH API ${res.status}`);
      const data = await res.json();
      return parseConditionNames(data);
    } catch {
      // Fall through to Firebase fallback
    }

    try {
      const functions = firebase.functions();
      const searchNihConditions = functions.httpsCallable('searchNihConditions', { timeout: 15000 });
      const { data } = await searchNihConditions({ terms: trimmed, maxList });
      return Array.isArray(data?.names) ? data.names : [];
    } catch (err) {
      console.error('[conditionsSearchService] fallback error:', err);
      return [];
    }
  },

  async searchConditionsWithCodes(query, maxResults = 20) {
    if (!query || typeof query !== 'string') return [];
    const trimmed = query.trim();
    if (trimmed.length === 0) return [];
    const maxList = Math.min(maxResults, 100);

    try {
      const url = `${NIH_CONDITIONS_URL}?terms=${encodeURIComponent(trimmed)}&df=consumer_name&cf=key_id&maxList=${maxList}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`NIH API ${res.status}`);
      const data = await res.json();
      if (!data || !Array.isArray(data)) return [];
      const displayArrays = Array.isArray(data[1]) ? data[1] : null;
      const codes = Array.isArray(data[3]) ? data[3] : null;
      if (!displayArrays || !codes) return [];
      return displayArrays.slice(0, codes.length).map((row, i) => {
        const str = Array.isArray(row) ? (row.length > 1 ? row[row.length - 1] : row[0]) : row;
        const displayName = str != null ? String(str).trim() : '';
        const raw = codes[i];
        const code = Array.isArray(raw) ? (raw[0] ?? raw) : raw;
        const conditionCode = code != null ? String(code).trim() : '';
        return { displayName, conditionCode };
      }).filter((o) => o.displayName);
    } catch (err) {
      console.warn('[conditionsSearchService] searchConditionsWithCodes error:', err);
      return [];
    }
  },

  async searchGeneticConditions(query, maxResults = 20) {
    if (!query || typeof query !== 'string') return [];
    const trimmed = query.trim();
    if (trimmed.length === 0) return [];
    const maxList = Math.min(maxResults, 500);

    try {
      const url = `${NIH_DISEASE_NAMES_URL}?terms=${encodeURIComponent(trimmed)}&df=DiseaseName&maxList=${maxList}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`NIH disease_names API ${res.status}`);
      const data = await res.json();
      return parseDiseaseNames(data);
    } catch (err) {
      console.warn('[conditionsSearchService] searchGeneticConditions error:', err);
      return [];
    }
  },
};
